import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as StarfinLogo } from '../images/stashfin-logo.svg';
import { ReactComponent as WarningIcon } from '../images/warning-icon.svg'
import { ReactComponent as CheckGreenIcon } from '../images/check-green-bg.svg'
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import BottomPopup from '../components/popups/BottomPopup';
import {
  setAccountNumber,
  setConfirmAccountNumber,
  setIfscCode,
  toggleChecked,
  openModal,
  closeModal,
  setBankDetails,
  setError,
  closeErrorModal,
  openErrorModal
} from '../lib/features/bankSlice';
import PageHeader from '../components/PageHeader';
import { useLazyGetIFSCDetailsQuery, useUpdateBankDetailsMutation } from '../lib/services/bankApi';
import { generateChecksum } from '../lib/middleware/generateChecksum';
import CenteredPopup from '../components/popups/CenteredPopup';
import { useTapClickMutation } from '../lib/middleware/TapClickApi';
import { pushCleverTapEvent } from "../lib/middleware/CtEvents";



const BankDetailsForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [updateBankDetails, { isLoading: isUpdating }] = useUpdateBankDetailsMutation()
  const [getIFSCDetails, { isLoading }] = useLazyGetIFSCDetailsQuery()
  const [tapClick] = useTapClickMutation()
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [showMsg, setShowMsg] = useState(true);
  //const [getStashCash] = useGetStashCashMutation()
  const location = useLocation()
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const jwt = useMemo(() => queryParams.get('jwt_token'), [queryParams])
  const secret = useMemo(() => queryParams.get('secret'), [queryParams])
  const footerRef = useRef(null);
  const [bottomPosition, setBottomPosition] = useState(10);
  const custSeg = localStorage.getItem('customerSegment')
  const handleClose = () => {
    dispatch(closeErrorModal(false));
    dispatch(setError(''))
  }
  const errorModalButtons = [
    { label: 'Close', onClick: handleClose }
  ]

  const {
    accountNumber,
    confirmAccountNumber,
    ifscCode,
    isChecked,
    isModalOpen,
    isErrorModalOpen,
    bankDetails,
    error
  } = useSelector((state) => state.bank);

  const formik = useFormik({
    initialValues: {
      accountNumber,
      confirmAccountNumber,
      ifscCode
    },
    validationSchema: Yup.object({
      accountNumber: Yup.string()
        .required('Bank account number is required')
        // .matches(/^\d{1,4}( \d{1,4}){0,3}$/, 'Only numbers are allowed and must be grouped in blocks of 4')
        .test(
          'valid-length',
          'Bank account number must contain between 1 and 25 digits',
          (value) => value && value.replace(/ /g, '').length <= 25
        ),
      confirmAccountNumber: Yup.string()
        .oneOf(
          [Yup.ref('accountNumber'), null],
          'Account numbers must match'
        )
        .required('Please re-enter your account number'),
      ifscCode: Yup.string()
        .required('IFSC Code is required')
        .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC Code format')
        .test(
          'valid-length',
          'IFSC ccode must be 11 digits',
          (value) => value && value.replace(/ /g, '').length === 11
        ),
    }),
    onSubmit: async (values) => {
      await fetchBankDetailsByIFSC(values.ifscCode)
      //dispatch(openModal());
      //dispatch(setBankDetails({ ifscCode: values.ifscCode, accountNumber: values.accountNumber }));
    },
  });

  const fetchBankDetailsByIFSC = async (ifsc) => {
    try {
      let jwt_token
      if (jwt) {
        jwt_token = jwt
        localStorage.setItem('jwt', jwt)
      } else {
        jwt_token = localStorage.getItem('jwt')
      }
      const scrt = secret ? secret : localStorage.getItem('secret')
      const checksum = generateChecksum({ mode: 'get_ifsc_details', ifsc })
      const result = await getIFSCDetails({ ifsc, scrt, jwt_token, checksum })
      if (result.data && result.data.status === 'error') {
        dispatch(setError(result.data.message))
        dispatch(openErrorModal())
      } else if (result.data && result.data.http_code === "201") {
        if (result.data.message === 'Login expired! Please re-login.') {
          localStorage.removeItem('jwt');
          localStorage.removeItem('lastVisitedPage');
          const phone = localStorage.getItem('phone')
          localStorage.clear()
          phone ? navigate(`/?phone=${phone}`) : navigate('/');
          
        } else {
          dispatch(setError(result.data.message))
          dispatch(openErrorModal())
        }
      } else if (result.data && result.data.message === 'Login expired! Please re-login.') {
        localStorage.removeItem('jwt');
        localStorage.removeItem('lastVisitedPage');
        const phone = localStorage.getItem('phone')
        localStorage.clear()
        phone ? navigate(`/?phone=${phone}`) : navigate('/');
        
      } else if (result.error) {
        dispatch(setError('Please check your internet or try again later.'))
      } else {
        dispatch(setBankDetails(result.data))
        dispatch(openModal())
      }
    } catch (error) {
      dispatch(setError('Something went wrong. Please try again later.'));
      dispatch(openErrorModal())
    }
  }

  const handleConfirm = async () => {
    try {
      const consents = [
        {
          consent_type: "SALARY_AND_BUSINESS_ACCOUNT",
          consent_status: isChecked ? "ACCEPTED" : "DECLINED",
          consent_text: "I confirm this is my salary/business account"
        }
      ];
      const requestData = {
        ifsc_code: bankDetails.IFSC,
        account_no: accountNumber.split(' ').join(''),
        meta_data: '',
        is_new: 0,
        lamf: '',
        mode: 'bank_details',
        product_id: '',
        consents: JSON.stringify(consents),
      }

      const checksum = generateChecksum(requestData)
      let jwt_token
      if (jwt) {
        jwt_token = jwt
        localStorage.setItem('jwt', jwt)
      } else {
        jwt_token = localStorage.getItem('jwt')
      }
      const scrt = secret ? secret : localStorage.getItem('secret');
      const result = await updateBankDetails({ ...requestData, checksum, jwt_token, scrt })
      if (result.data && result.data.status === 'error') {
        if (result.data.message === 'Login expired! Please re-login.') {
          localStorage.removeItem('jwt');
          localStorage.removeItem('lastVisitedPage');
          const phone = localStorage.getItem('phone')
          localStorage.clear()
          phone ? navigate(`/?phone=${phone}`) : navigate('/');
          
        } else {
          dispatch(setError(result.data.message))
          dispatch(openErrorModal())
        }
      } else if (result.error) {
        dispatch(setError('Please check your internet or try again later.'))
        dispatch(openErrorModal())
      } else if (result.data && result.data.status === 'success') {
        //localStorage.setItem("lastVisitedPage", '/take-selfie')
        //navigate('/take-selfie');
        const event_name = 'web_Bank_verification'
        const event_property = { Source: window.localStorage.getItem('utm_source'), segment: result.data.customer_segmentation, 'A/C number': requestData.account_no.toString().slice(-4), ifsc: requestData.ifsc_code, CTA: 'confirm details', Action: 'Click' }
        await pushCleverTapEvent({ event_name, event_property })
        if (result.data.redirect_url) {
          window.location.href = result.data.redirect_url
        }
      }
    } catch (error) {
      dispatch(setError('Something went wrong. Please try again later.'));
      dispatch(openErrorModal())
    }
  };
  useEffect(() => {
    //const viewport = window.visualViewport;
    const updateFooterPosition = () => {
      let height = window.visualViewport.height;
      if (!/iPhone|iPad|iPod|Android/.test(window.navigator.userAgent)) {
        height = window.visualViewport.height;
      }
      setBottomPosition(height - window.visualViewport.height + 10);
    }
    updateFooterPosition();
    window.visualViewport.addEventListener("resize", updateFooterPosition);
    window.visualViewport.addEventListener("scroll", updateFooterPosition);

    return () => {
      window.visualViewport.removeEventListener("resize", updateFooterPosition);
      window.visualViewport.removeEventListener("scroll", updateFooterPosition);
    };
  }, []);

  useEffect(() => {
    if (showMsg) {
      setTimeout(() => {
        setShowMsg(false)
      }, 3000);
    }
  }, [showMsg])

  return (
    <div className={`external-wrapper bank-details ${isKeyboardOpen ? 'keyboard-active' : ''}`}>
      <CenteredPopup
        isOpen={isErrorModalOpen}
        buttons={errorModalButtons}
        onClose={handleClose} icon={<WarningIcon />}>
        <p>{error}</p>
      </CenteredPopup>
      <div className='page-content'>
        <PageHeader />
        {(!custSeg && showMsg) || (custSeg && custSeg.toLowerCase() !== 'a1-a2' && showMsg) ? <div className="success-tag"><CheckGreenIcon /> Documents collected successfully</div> : null}
        <div className='page-heading'>
          <h2 className='page-title'>Enter your bank account details</h2>
        </div>
        <p>Enter your salary/business account to receive funds.</p>
        <form onSubmit={formik.handleSubmit}
          onBlur={(e) => {
            formik.handleBlur(e);
            setTimeout(() => {
              if (!document.activeElement || document.activeElement.tagName !== "INPUT") {
                setIsKeyboardOpen(false);
              }
            }, 100);
          }}
          onFocus={() => setIsKeyboardOpen(true)}>
          <div className="input-wrapper">
            <input
              autoComplete='off'
              type="password"
              name="accountNumber"
              placeholder=" "
              style={{ fontSize: '24px' }}
              inputMode="numeric"
              className={`form-control`}
              value={formik.values.accountNumber}
              maxLength={31}
              onChange={(e) => {
                const value = e.target.value ? e.target.value.replace(/[^0-9]/g, "") : '';
                const formattedValue = value ? value
                  .match(/.{1,4}/g)
                  ?.join(" ")
                  .slice(0, 31) : '';
                formik.setFieldValue("accountNumber", formattedValue);
                dispatch(setAccountNumber(formattedValue));
              }}
              onBlur={formik.handleBlur}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              disabled={!isChecked}
            />
            <label>Bank account number*</label>
            {formik.errors.accountNumber && formik.touched.accountNumber && (
              <div className="error">{formik.errors.accountNumber}</div>
            )}
          </div>

          <div className="input-wrapper">
            <input
              type="text"
              autoComplete='off'
              name="confirmAccountNumber"
              inputMode="numeric"
              placeholder=" "
              className={`form-control`}
              value={formik.values.confirmAccountNumber}
              maxLength={31}
              onChange={(e) => {
                const value = e.target.value ? e.target.value.replace(/[^0-9]/g, "") : '';
                const formattedValue = value ? value
                  .match(/.{1,4}/g)
                  ?.join(" ")
                  .slice(0, 31) : '';
                formik.setFieldValue("confirmAccountNumber", formattedValue);
                dispatch(setConfirmAccountNumber(formattedValue));
              }}
              onBlur={formik.handleBlur}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              disabled={!isChecked}
            />
            <label>Re-enter your bank account number*</label>
            {formik.errors.confirmAccountNumber && formik.touched.confirmAccountNumber && (
              <div className="error">{formik.errors.confirmAccountNumber}</div>
            )}
          </div>

          <div className="input-wrapper">
            <input
              type="text"
              name="ifscCode"
              autoComplete='off'
              placeholder=" "
              maxLength={11}
              className={`form-control`}
              value={formik.values.ifscCode}
              onChange={(e) => {
                //formik.handleChange(e);
                const value = e.target.value
                const formattedValue = value ? value.replace(/[^A-Za-z0-9]/g, "").slice(0, 11) : ''
                formik.setFieldValue("ifscCode", (formattedValue).toUpperCase());
                dispatch(setIfscCode((formattedValue).toUpperCase()));
              }}
              onBlur={formik.handleBlur}
              disabled={!isChecked}
            />
            <label>IFSC Code*</label>
            {formik.errors.ifscCode && formik.touched.ifscCode && (
              <div className="error">{formik.errors.ifscCode}</div>
            )}
          </div>
          <div className='bottom-footer p-0' ref={footerRef} style={{ bottom: `${bottomPosition}px` }}>
            <div className='consent-check'>
              <label>
                <div className={`checkbox ${isChecked ? 'checked' : ''}`}>
                  <input type="checkbox" checked={isChecked} onChange={() => dispatch(toggleChecked())} />
                </div>
                <p>I confirm this is my salary/business account</p>
              </label>
            </div>
            <p className='powered-by'>Powered by <StarfinLogo /></p>
            <button type="submit" className={`btn btn-primary ${!isChecked || isLoading ? 'disabled' : ''}`}>Continue</button>
          </div>
        </form>
      </div>
      <BottomPopup
        isOpen={isModalOpen}
        onClose={() => dispatch(closeModal())}
        title="Bank Details"
        content={bankDetails}
        buttons={[{ label: 'Confirm Details', onClick: handleConfirm, disabled: isUpdating }]}>
        {bankDetails && <ul className='bank-details'>
          <li><span>Bank Name :</span><span>{bankDetails.BANK}</span></li>
          <li><span>Account Number :</span><span>{accountNumber}</span></li>
          <li><span>IFSC Code :</span><span>{bankDetails.IFSC}</span></li>
          <li className='address'><span>Branch Address:</span><span>{bankDetails.ADDRESS}</span></li>
        </ul>}
      </BottomPopup>
    </div>
  );
};

export default BankDetailsForm;
